import { debugLog } from '../debugLog';

export interface PerformanceElementTiming extends PerformanceEntry {
    identifier: string;
    element: HTMLElement | SVGElement | null;
    renderTime: number;
}

function isElementTimingEntry(entry: PerformanceEntry): entry is PerformanceElementTiming {
    return entry.entryType === 'element';
}

export type ElementLoadedEvent = {
    identifier: string;
    renderTimeFromLastNavigation: number;
    renderTime: number;
    element: HTMLElement | SVGElement | null;
    pageChangedTimestamp: number;
};

type IsEntryValidCallback = (entry: PerformanceEntry) => boolean;

/**
 * Класс для сбора метрики Element Timing.
 *
 * Метрика ElementTiming измеряет время от загрузки страницы (включая SPA переходы) до отрисовки "hero" элемента,
 * после отрисовки которого мы считаем, что страница загружена и с ней можно работать
 */
export class ElementTimingObserver {
    private observer: PerformanceObserver;
    private elementTimingSent = new Set<string>();
    private pageChangedTimestamp = 0;
    private onceSubscribers = new Set<(e: ElementLoadedEvent) => void>();
    private subscribers = new Set<(e: ElementLoadedEvent) => void>();
    // Последние события текущей (под)страницы — для buffered-реплея в awaitElementTiming
    private lastEvents = new Map<string, ElementLoadedEvent>();

    constructor(private isEntryValid: IsEntryValidCallback) {
        // Подписываемся на появление элементов с аттрибутом elementtiming
        this.observer = new PerformanceObserver(list => {
            list.getEntries().filter(isElementTimingEntry).forEach(this.handlePerformanceElementTiming);
        });
    }

    private handlePerformanceElementTiming = (entry: PerformanceElementTiming) => {
        // Фильтруем невалидные записи (например, попавшие в фон или сгенерированные во время скрытия страницы)
        if (!this.isEntryValid(entry)) {
            return;
        }

        const renderTimeFromLastNavigation = entry.renderTime - this.pageChangedTimestamp;

        const event: ElementLoadedEvent = {
            identifier: entry.identifier,
            renderTimeFromLastNavigation,
            renderTime: entry.renderTime,
            element: entry.element,
            pageChangedTimestamp: this.pageChangedTimestamp,
        };

        this.lastEvents.set(entry.identifier, event);

        this.subscribers.forEach(subscriber => {
            subscriber(event);
        });

        // Не вызываем одноразовые подписчики повторно
        // (например, при виртуализации, поиске, etc.) - не отсылаем метрику Hero элемента повторно
        if (this.elementTimingSent.has(entry.identifier)) {
            return;
        }
        this.elementTimingSent.add(entry.identifier);

        this.onceSubscribers.forEach(subscriber => {
            subscriber(event);
        });
    };

    /**
     * Последнее событие отрисовки элемента в рамках текущей (под)страницы.
     */
    public getLastEvent(identifier: string): ElementLoadedEvent | undefined {
        return this.lastEvents.get(identifier);
    }

    /**
     * Переданная функция будет вызвана при отрисовке элемента
     */
    public subscribe(onElementRender: (e: ElementLoadedEvent) => void) {
        this.subscribers.add(onElementRender);

        return () => {
            this.subscribers.delete(onElementRender);
        };
    }

    /**
     * Переданная функция будет вызвана только один раз для элемента
     */
    public subscribeOnceByElementIdentifier(onElementRender: (e: ElementLoadedEvent) => void) {
        this.onceSubscribers.add(onElementRender);

        return () => {
            this.onceSubscribers.delete(onElementRender);
        };
    }

    /**
     * Перезапускает observer с указанным временем начала страницы.
     * Очищает состояние.
     * @param pageStartTime временная метка начала страницы (значение из performance.now())
     */
    restart({ pageChangedTimestamp }: { pageChangedTimestamp: number }) {
        // disconnect перед очисткой состояния — отменяет запланированные,
        // но ещё не выполненные callback'и PerformanceObserver
        this.observer.disconnect();
        this.pageChangedTimestamp = pageChangedTimestamp;
        this.elementTimingSent = new Set<string>();
        // События прошлой подстраницы не должны считаться отрисовкой на новой
        this.lastEvents = new Map<string, ElementLoadedEvent>();
        this.observer.observe({ type: 'element', buffered: false });
    }

    start() {
        this.observer.observe({ type: 'element', buffered: true });
    }

    /**
     * Приостанавливает сбор новых метрик.
     */
    stop() {
        debugLog(() => ({ tags: ['ElementTiming', 'pause'] }));
        this.observer.disconnect();
    }

    /**
     * Продолжает сбор метрик
     */
    continue() {
        this.observer.takeRecords();
        this.observer.observe({ type: 'element', buffered: false });
    }
}
